import { useClub } from '../contexts/ClubContext';
import SubscriptionBlocked from '../pages/SubscriptionBlocked';
import SubscriptionWarningBanner from './SubscriptionWarningBanner';

/**
 * SubscriptionGuard - Wraps pages to enforce subscription status
 * 
 * Behavior:
 * - active: Full access
 * - past_due: Shows warning banner but allows access
 * - suspended: Blocks all access, shows payment required screen
 * - cancelled: Blocks all access
 */
function SubscriptionGuard({ children, requiresActive = false }) {
  const { 
    club,
    loading,
    isSubscriptionActive,
    isSubscriptionPastDue,
    isSubscriptionSuspended,
    isSubscriptionCancelled,
    getDaysOverdue,
    GRACE_PERIOD_DAYS
  } = useClub();

  // Still loading
  if (loading) {
    return null;
  }

  // No club loaded
  if (!club) {
    return children;
  }

  // Check subscription status
  const subscriptionStatus = {
    active: isSubscriptionActive(),
    pastDue: isSubscriptionPastDue(),
    suspended: isSubscriptionSuspended(),
    cancelled: isSubscriptionCancelled()
  };

  // Suspended or cancelled - block access
  if (subscriptionStatus.suspended || subscriptionStatus.cancelled) {
    return <SubscriptionBlocked status={subscriptionStatus.cancelled ? 'cancelled' : 'suspended'} />;
  }

  // If page requires active subscription and we're past due, block
  if (requiresActive && subscriptionStatus.pastDue) {
    return <SubscriptionBlocked status="past_due" />;
  }

  // Past due - show warning banner but allow access
  if (subscriptionStatus.pastDue) {
    const daysOverdue = getDaysOverdue();
    const daysRemaining = GRACE_PERIOD_DAYS - daysOverdue;
    
    return (
      <>
        <SubscriptionWarningBanner daysRemaining={daysRemaining} />
        {children}
      </>
    );
  }

  // Active - full access
  return children;
}

export default SubscriptionGuard;
